import React, { useState } from 'react';
import { logDebug, logError } from 'utils/logger';
import { useSelector } from 'react-redux';
import { toast } from 'utils/toast';
import { LOCATION_MESSAGES } from 'locales/en-US';
import { safeLogEvent } from 'firebase.js';
import { getLocationWithFallback } from 'utils/nativeBridge';
import GoogleMap from 'components/GoogleMap/GoogleMap';
import Modal from 'components/Modal';
import LocationPermissionModal from 'components/LocationPermissionModal';
import useLocationPermission from 'hooks/useLocationPermission';
import { useTeamColors } from 'hooks';
import {
  GameInfoLinkContainer,
  DirectionButton,
  DisabledMessage,
} from '../style';

export const MapWithDirection = ({
  destinationCoords,
  stadium,
  onClose,
  show,
}) => {
  const { mainColor } = useTeamColors();
  const [mapKey, setMapKey] = useState(0);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const requestingRef = React.useRef(false);

  // Check if location services are enabled - handle both string '1' and integer 1
  const configData = useSelector((state) => state.configuration?.data);
  const isLocationServicesEnabled =
    configData?.location_services_enabled === 1 ||
    configData?.location_services_enabled === '1';

  const {
    incrementPromptCount,
    setPermissionGranted,
    setPermissionDenied,
    isLocationDenied,
    isThirdPrompt,
  } = useLocationPermission();

  // Force map to re-render when modal opens
  React.useEffect(() => {
    if (show) {
      setTimeout(() => {
        setMapKey((prev) => prev + 1);
      }, 50);
    }
  }, [show]);

  const handlePermissionAllow = () => {
    setShowPermissionModal(false);

    // Trigger browser permission request FIRST
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          // User granted browser permission
          setPermissionGranted();
          toast.success(LOCATION_MESSAGES.enabled);
          safeLogEvent('location_permission_granted', {
            context: 'permission_modal',
          });
        },
        (error) => {
          // User denied browser permission
          if (error.code === 1) {
            setPermissionDenied();
            toast.error(LOCATION_MESSAGES.enableInSettings);
            safeLogEvent('location_permission_denied', {
              context: 'browser_prompt',
            });
          }
        }
      );
    }
  };

  const handlePermissionDeny = () => {
    setShowPermissionModal(false);
    setPermissionDenied();
    safeLogEvent('location_permission_denied', {
      context: 'permission_modal',
    });
    // Just dismiss - keep it simple
  };

  const handlePermissionMaybeLater = () => {
    incrementPromptCount(); // Increment when user clicks Maybe Later
    setShowPermissionModal(false);
    // Don't call setMaybeLater() - it overwrites the increment with stale state
    safeLogEvent('location_permission_maybe_later', {
      context: 'permission_modal',
    });
  };

  const getDirection = async () => {
    logDebug('getDirection called', {
      isLocationServicesEnabled,
      isLocationDenied: isLocationDenied(),
    });

    // If location services are disabled, don't request location
    if (!isLocationServicesEnabled) {
      logDebug('Location services disabled by feature flag');
      toast.info(LOCATION_MESSAGES.notAvailable);
      return;
    }

    if (requestingRef.current) {
      logDebug('Already requesting location, returning');
      return;
    }

    // Check if location is denied
    if (isLocationDenied()) {
      logDebug('Location denied, showing error');
      toast.error(LOCATION_MESSAGES.servicesDisabled);
      safeLogEvent('directions_click', {
        status: 'denied',
      });
      return;
    }

    logDebug('Requesting location via native bridge...');
    requestingRef.current = true;

    try {
      const locationResult = await getLocationWithFallback();

      if (locationResult.success) {
        const { latitude, longitude } = locationResult.location;
        const url = `https://www.google.com/maps/dir/${latitude},${longitude}/${destinationCoords.lat},${destinationCoords.lng}`;
        logDebug('Opening directions URL', {
          url,
          source: locationResult.location.source,
        });

        safeLogEvent('directions_click', {
          status: 'success',
          source: locationResult.location.source || 'unknown',
        });

        window.open(url, '_blank');
      } else {
        throw new Error(locationResult.error);
      }
    } catch (error) {
      logError('Location request failed', error);

      let errorMessage = 'Unable to retrieve your location';

      if (error.message.includes('timeout')) {
        errorMessage = 'Location request timed out. Please try again.';
      } else if (error.message.includes('permission')) {
        errorMessage =
          'Location access denied. Please enable location in app settings.';
      } else if (error.message.includes('unavailable')) {
        errorMessage = 'Location services not available in this environment.';
      }

      toast.error(errorMessage);
      safeLogEvent('select_content', {
        content_type: 'error',
        content_id: 'geolocation_error',
        error_message: error.message,
      });
    } finally {
      requestingRef.current = false;
    }
  };

  const isButtonDisabled = isLocationDenied();

  const handleHelpClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const searchUrl =
      'https://www.google.com/search?q=how+to+enable+location+services+in+my+browser';
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
    safeLogEvent('location_help_clicked', {
      context: 'disabled_button',
    });
  };

  return (
    <>
      <Modal onClose={onClose} show={show}>
        {show ? <GoogleMap key={mapKey} location={destinationCoords} /> : null}
        <GameInfoLinkContainer>
          <DirectionButton
            $color={mainColor}
            disabled={isButtonDisabled}
            onClick={getDirection}
            title={
              isButtonDisabled
                ? 'Enable location in browser settings to get directions'
                : ''
            }
            type="button"
          >
            Directions to {stadium}...
          </DirectionButton>
          {isButtonDisabled ? (
            <DisabledMessage>
              Location services are disabled.{' '}
              <button
                onClick={handleHelpClick}
                style={{
                  color: '#FFF',
                  textDecoration: 'underline',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                }}
                type="button"
              >
                How to enable?
              </button>
            </DisabledMessage>
          ) : null}
        </GameInfoLinkContainer>
      </Modal>

      <LocationPermissionModal
        isThirdPrompt={isThirdPrompt}
        onAllow={handlePermissionAllow}
        onDeny={handlePermissionDeny}
        onMaybeLater={handlePermissionMaybeLater}
        show={showPermissionModal}
      />
    </>
  );
};
